import Core                   from '@/core/Core';
import CharacteristicUnit     from '@/domains/CharacteristicUnits/CharacteristicUnit';
import CharacteristicUnitsDAO from '@/domains/CharacteristicUnits/CharacteristicUnitsDAO';
import ContextMenuItem        from '@/lib/components/VContextMenu/ContextMenuItem';

import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';

class CharacteristicUnitsService {

  static async get(id: number): Promise<CharacteristicUnit | null> {
    const result = await this.getList({ id });
    return result[0] ?? null;
  }

  static async getListPaginated(
    params?: Parameters<typeof CharacteristicUnitsDAO.getList>[0] & {
      pagination?: RpcDataPagination,
      sort?: RpcDataSort,
    }
  ): Promise<{ data: CharacteristicUnit[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await CharacteristicUnitsDAO.getList(filters, pagination, sort);

    return {
      data: Core.utils.hydrateArray(response.data, CharacteristicUnit.fromServer),
      pagination: response.meta.pagination,
    };
  }

  static async getList(
    params?: Parameters<typeof CharacteristicUnitsDAO.getList>[0]
  ): Promise<CharacteristicUnit[]> {
    const result = await this.getListPaginated(params);
    return result.data;
  }

  static async add(characteristicUnit: CharacteristicUnit): Promise<boolean> {
    const response = await CharacteristicUnitsDAO.add(characteristicUnit);

    if (response.ok) {
      Core.showNotification.success('Единица измерения добавлена');
    }

    return response.ok;
  }

  static async update(characteristicUnit: CharacteristicUnit): Promise<boolean> {
    const response = await CharacteristicUnitsDAO.update(characteristicUnit);

    if (response.ok) {
      Core.showNotification.success('Единица измерения обновлена');
    }

    return response.ok;
  }

  static async delete(id: number): Promise<boolean> {
    const response = await CharacteristicUnitsDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success('Единица измерения удалена');
    }

    return response.ok;
  }

  /**
   * Удаление с подтверждением. Используется из контекстного меню.
   * Старый delete(id) оставлен для массовых операций без UX.
   */
  static async deleteWithConfirmation(characteristicUnit: CharacteristicUnit): Promise<boolean> {
    const confirmed = await Core.dialogs.confirm(
      'Удалить эту единицу измерения?',
      characteristicUnit.name,
    );

    if (!confirmed) return false;

    return await this.delete(characteristicUnit.id);
  }

  /**
   * Открыть карточку единицы измерения характеристики.
   * Промис резолвится по закрытию карточки.
   */
  static showCard(characteristicUnit: CharacteristicUnit | null): Promise<boolean> {
    if (characteristicUnit) {
      return Core.dialogs.CharacteristicUnitCard.showAsPromise({ entity: characteristicUnit });
    }
    else {
      return Core.dialogs.CharacteristicUnitCard.showAsPromise({});
    }
  }

  // ============================================================
  // Контекстное меню
  // ============================================================

  /**
   * Пункты контекстного меню единицы измерения характеристики для грида
   * реестра и карточки. Передаётся в VDataGridV2 / VDialogV3 пропом :context-menu.
   *
   * callback возвращает промис — грид дождётся завершения перед refresh.
   *   - showCard               → Promise<boolean> (резолв по закрытию карточки)
   *   - deleteWithConfirmation → Promise<boolean>
   *   - История действий       → void, список не меняет → refreshAfter: false
   */
  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (unit: CharacteristicUnit) => { return this.showCard(unit); },
        null,
        false,
        true,
        true,
      ),
      new ContextMenuItem(
        'История действий',
        'history',
        (unit: CharacteristicUnit) => { Core.dialogs.FeedDialog.show({ entityTypeName: 'characteristicUnit', entityId: unit.id, showAddComment: false }); },
        null,
        false,
        false,
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (unit: CharacteristicUnit) => { return this.deleteWithConfirmation(unit); },
        null,
        false,
        true,
        true,
      ),
    ];
  }
}

export default CharacteristicUnitsService;
